import axios, { AxiosError, AxiosRequestHeaders } from "axios";
import config from "config";
import { trackAxiosErrorWithSentry } from "sentry";

import { getAPIUrl } from "shared/utils/api-utils";
import { defaultHeaders, getAuthHeaders } from "shared/utils/auth-utils";

declare module "axios" {
  // Allow callers to opt out of Sentry reporting for specific response
  // statuses on a per-request basis (e.g. 404s on an endpoint where a missing
  // resource is an expected, handled outcome).
  export interface AxiosRequestConfig {
    skipSentryReportStatuses?: number[];
  }
}

const shouldSkipSentryReport = (error: AxiosError): boolean => {
  const status = error.response?.status;
  const skipStatuses = error.config?.skipSentryReportStatuses;

  return !!status && !!skipStatuses?.includes(status);
};

const getVersionedURL = (version = config.API_CURRENT_VERSION) =>
  `${getAPIUrl("api")}/${version}`;

const getV2URL = () => getVersionedURL("v2");

const getPydanticAIURL = () => `${getAPIUrl("ai")}/pydanticai`;

const clientWithoutVersion = axios.create({
  baseURL: getAPIUrl("api"),
});

const client = axios.create({
  baseURL: getVersionedURL(),
});

const clientV2 = axios.create({ baseURL: getV2URL() });

const clientServerPydanticAI = axios.create({
  baseURL: getPydanticAIURL(),
});

client.interceptors.request.use((config) => ({
  ...config,
  headers: {
    ...defaultHeaders,
    ...getAuthHeaders(),
    ...config.headers,
  } as AxiosRequestHeaders,
  withCredentials: true,
}));

client.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (!shouldSkipSentryReport(error)) {
      trackAxiosErrorWithSentry(error, "api-client");
    }

    return Promise.reject(error);
  }
);

clientV2.interceptors.request.use((config) => ({
  ...config,
  headers: {
    ...defaultHeaders,
    ...getAuthHeaders(),
    ...config.headers,
  } as AxiosRequestHeaders,
  withCredentials: true,
}));

clientV2.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (!shouldSkipSentryReport(error)) {
      trackAxiosErrorWithSentry(error, "api-client");
    }

    return Promise.reject(error);
  }
);

clientServerPydanticAI.interceptors.request.use((config) => ({
  ...config,
  headers: {
    ...defaultHeaders,
    ...getAuthHeaders(),
    ...config.headers,
  } as AxiosRequestHeaders,
  withCredentials: true,
}));

clientServerPydanticAI.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (!shouldSkipSentryReport(error)) {
      trackAxiosErrorWithSentry(error, "ai_server_pydanticai");
    }

    return Promise.reject(error);
  }
);

clientWithoutVersion.interceptors.request.use((config) => ({
  ...config,
  headers: {
    ...defaultHeaders,
    ...getAuthHeaders(),
    ...config.headers,
  } as AxiosRequestHeaders,
  withCredentials: true,
}));

export { clientServerPydanticAI, clientWithoutVersion, clientV2 };
export default client;
